import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import '../App.css';
import '../Tabs.css';
import AutocompletePaginado from './AutocompletePaginado';
import Button from '@mui/material/Button';
import BackIcon from '@mui/icons-material/Reply';
import 'rsuite/DatePicker/styles/index.css';
import { aplicarMascaraCpfCnpj } from '../utils/MaskUtils';
import { handleErrors, request } from '../utils/ApiUtils';
import Input from './Input';
import { showNotification } from '../utils/NotificationUtils';
import { getActualDate } from '../utils/DateUtils';
import InputMonetary from './InputMonetary';
import ContratoParcelaTab from './ContratoParcelaTab';
import ContratoParcelaFechadorTab from './ContratoFechadorTab';
import ContratoParcelaCaptadorTab from './ContratoCaptadorTab';
import { hasAnyRole } from '../utils/TokenUtils';
import { rolesMapping } from '../utils/RolesUtils';

function ContratoForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [contrato, setContrato] = useState({ data: getActualDate(), vencimentoPrimeiraParcela: getActualDate() })
    const saveTimeoutRef = useRef(null);
    const [error, setError] = useState({})
    const [fetchListError, setFetchListError] = useState();

    const [isStatusCancelado, setIsStatusCancelado] = useState(false);
    const [comissoes, setComissoes] = useState({})
    const [isSaving, setIsSaving] = useState(false);

    const [refreshKey, setRefreshKey] = useState(0);

    const triggerRefresh = () => setRefreshKey((prev) => prev + 1);

    const [activeTab, setActiveTab] = useState("abaParcelamentoContrato");

    const handleTabClick = (tabId) => {
        setActiveTab(tabId);
    };

    useEffect(() => {
        loadContrato();
    }, [id]);

    useEffect(() => {
        if (contrato?.semComissao) {
            setContrato((prev) => ({
                ...prev,
                fechador: null,
                porcentagemComissaoFechador: null,
                captador: null,
                porcentagemComissaoCaptador: null
            }));

            setActiveTab("abaParcelamentoContrato");
        }

    }, [contrato?.semComissao]);

    useEffect(() => {
        if (naoPossuiPorcentagem(contrato?.porcentagemComissaoCaptador) && activeTab == "abaParcelamentoCaptador") {
            setActiveTab("abaParcelamentoContrato");
        }
    }, [contrato?.porcentagemComissaoCaptador]);

    useEffect(() => {
        if (naoPossuiPorcentagem(contrato?.porcentagemComissaoFechador) && activeTab == "abaParcelamentoFechador") {
            setActiveTab("abaParcelamentoContrato");
        }
    }, [contrato?.porcentagemComissaoFechador]);

    const naoPossuiPorcentagem = (value) => {
        return !value || value == '' || value <= 0;
    }

    const loadContrato = async () => {
        if (id) {
            request(`contratos/${id}`, {
                type: 'GET',
                callback: (data) => (
                    setContrato(data),
                    setIsStatusCancelado(data?.status?.cancelado)
                )
            })

            findComissoes();
        } else {
            setContrato({ data: getActualDate(), vencimentoPrimeiraParcela: getActualDate() });
        }
    }

    const findComissoes = async (isRefresh) => {
        if (id)
            request(`contrato-parcela/${id}/comissoes`, {
                type: 'GET',
                callback: (data) => (
                    setComissoes(data)
                ),
                callbackError: (message) => setError(message || 'Ocorreu um erro inesperado, tente novamente ou contate o suporte.'),
                finally: !isRefresh && agendarRefresh()

            })
    };

    const agendarRefresh = (time = 2000) => {
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current); // Reseta o temporizador existente
        }

        saveTimeoutRef.current = setTimeout(() => triggerRefresh(), time);
    };

    const salvarContrato = async () => {
        setIsSaving(true);

        request('contratos', {
            type: 'POST',
            body: JSON.stringify(contrato),
            callback: (data) => {
                showNotification('Contrato salvo', 'success');
                setContrato(Object.assign({}, data));
                setIsStatusCancelado(data?.status?.cancelado)
                if (!id) navigate(`/contratos/cadastro/${data.id}`);
            },
            callbackError: (responseData, message) => { handleErrors(responseData, message, { setError }) },
            finally: () => {
                triggerRefresh();
                setIsSaving(false);
                findComissoes();
            }
        })
    };

    const atualizarStatus = async () => {
        setIsSaving(true);

        request(`contratos/${contrato.id}/updateStatus`, {
            type: 'PUT',
            body: JSON.stringify(contrato?.status),
            callback: (data) => {
                showNotification('Status atualizado com sucesso', 'success');
                setContrato(Object.assign({}, data));
                setIsStatusCancelado(data?.status?.cancelado)
                if (!id) navigate(`/contratos/cadastro/${data.id}`);
            },
            callbackError: (responseData, message) => { handleErrors(responseData, message, { setError: setError }) },
            finally: () => {
                triggerRefresh();
                setIsSaving(false);
                findComissoes();
            }
        })
    };

    const handleInputChange = async (campo, valor) => {
        error[campo] = undefined;
        setError(error)

        contrato[campo] = valor;
        setContrato({ ...contrato });
    };

    const handleChangeColaboradorComissao = (campoColaborador, campoPorcentagem, itemSelecionado) => {
        const colaborador = itemSelecionado?.item;

        setError({ ...error, [campoColaborador]: undefined });
        setContrato((prev) => ({
            ...prev,
            [campoColaborador]: colaborador,
            [campoPorcentagem]: colaborador?.[campoPorcentagem] ?? null
        }));
    };

    const handleChangeCondicaoPagamento = async (valor) => {
        setError({ ...error, condicaoPagamento: undefined })

        const isPagamentoAVista = valor?.id == 1;

        const quantidadeParcelas = isPagamentoAVista ? undefined : contrato?.quantidadeParcelas;

        setContrato((prev) => ({
            ...prev,
            condicaoPagamento: valor,
            quantidadeParcelas: quantidadeParcelas,
        }));
    };

    const isCondicaoPagamentoAVista = () => {
        return contrato?.condicaoPagamento?.id == 1;
    }

    const tabCaptador = () => (
        <div
            className={`tab ${activeTab === "abaParcelamentoCaptador" ? "active" : ""}`}
            onClick={() => handleTabClick("abaParcelamentoCaptador")}
        >
            Captador
        </div>
    )

    const tabFechador = () => (
        <div
            className={`tab ${activeTab === "abaParcelamentoFechador" ? "active" : ""}`}
            onClick={() => handleTabClick("abaParcelamentoFechador")}
        >
            Fechador
        </div>
    )

    const tabs = () => (
        <div>
            <div className="tabs">
                <div
                    className={`tab ${activeTab === "abaParcelamentoContrato" ? "active" : ""}`}
                    onClick={() => handleTabClick("abaParcelamentoContrato")}
                >
                    Contrato
                </div>
                {!contrato?.semComissao && !naoPossuiPorcentagem(contrato?.porcentagemComissaoCaptador) && tabCaptador()}
                {!contrato?.semComissao && !naoPossuiPorcentagem(contrato?.porcentagemComissaoFechador) && tabFechador()}

            </div>

            {activeTab === "abaParcelamentoContrato" && <div className={`tab-content active}`}>
                <ContratoParcelaTab contrato={contrato} comissoes={comissoes} findTotals={findComissoes} loadContrato={loadContrato} contratoId={contrato?.id} refreshKey={refreshKey} contratoCancelado={isStatusCancelado} />
            </div>}

            {activeTab === "abaParcelamentoCaptador" && <div className={`tab-content active}`}>
                <ContratoParcelaCaptadorTab contrato={contrato} comissoes={comissoes} findTotals={findComissoes} contratoId={contrato?.id} refreshKey={refreshKey} contratoCancelado={isStatusCancelado} />

            </div>}

            {activeTab === "abaParcelamentoFechador" && <div className={`tab-content active}`}>
                <ContratoParcelaFechadorTab contrato={contrato} comissoes={comissoes} findTotals={findComissoes} contratoId={contrato?.id} refreshKey={refreshKey} contratoCancelado={isStatusCancelado} />
            </div>}
        </div>
    );

    return (
        <div className="form-container">

            <div id="notification-container" className='notification-container' style={{
                position: "fixed",
                display: "flex,",
                flexDirection: "column-reverse",
                justifyContent: "flex-end",
                flexWrap: "wrap-reverse",
                alignItems: "flex-end",
                bottom: "0,",
                right: "30px",
                maxHeight: "100vh"
            }}></div>

            <div className="header-container">
                <h1 style={{ color: 'black', fontWeight: 'bold' }}>Contrato</h1>
                <Button variant="text" onClick={() => navigate(`/contratos`)} startIcon={(<BackIcon style={{ fontSize: '2rem', fontWeight: 'bold', color: '#0057a3' }} />)} />
            </div>

            <div className='header-body'>
                <h4 style={{ color: 'black', display: 'inline-block', textShadow: '0 6px 6px rgba(122, 116, 182, 0.5)' }}>Dados do contrato</h4>
            </div>

            <div className={isStatusCancelado ? 'row' : 'row-with-auto-complete'}>
                <Input id="numero"
                    value={contrato?.numero}
                    parameters={{ error: error.numero, width: '8%', label: "Nº do Contrato*", disabled: true, onChange: (value) => handleInputChange('numero', value) }} />

                <Input id="numeroProcesso"
                    value={contrato?.numeroProcesso}
                    parameters={{ error: error.numeroProcesso, width: '24%', label: "Nº do Processo*", disabled: isStatusCancelado, onChange: (value) => handleInputChange('numeroProcesso', value) }} />

                <AutocompletePaginado label={"Cliente*"}

                    parameters={{
                        width: '32%',
                        label: "Cliente*",
                        bodyToSearch: { somenteAtivos: true },
                        error: error?.cliente,
                        disabled: isStatusCancelado,
                        getLabel: ((item) => (item.nome + (item.documento ? ` - ${aplicarMascaraCpfCnpj(item.documento)}` : ''))),
                        onChange: (clienteSelecionado) => handleInputChange('cliente', clienteSelecionado?.item),
                        value: contrato?.cliente,
                        title: (contrato?.cliente ? `Documento: ${contrato?.cliente.documento} 
                                \nTelefone: ${contrato?.cliente.telefone || ''} 
                                \nE-mail: ${contrato?.cliente.email || ''}
                                \nEndereço: ${contrato?.cliente?.endereco?.cidade} - ${contrato?.cliente?.endereco?.uf}`
                            : '')
                    }}
                    endpoint={"clientes/paginated"} />

                <Input id="cidade" value={contrato?.cliente?.endereco?.cidade} parameters={{ disabled: true, width: '20%', label: "Cidade" }} />

                <Input id="uf" value={contrato?.cliente?.endereco?.uf} parameters={{ disabled: true, width: '5%', label: "UF" }} />
            </div>

            <div className='row-with-auto-complete' >
                <div className="checkbox-group" style={{ width: '18%' }}>
                    <input type="checkbox" id="captador" name="semComissao" disabled={isStatusCancelado} checked={contrato?.semComissao} onClick={() => handleInputChange('semComissao', !contrato?.semComissao)} />
                    <label htmlFor="semComissao" className="checkbox-label">Contrato sem comissão</label>
                </div>

                <AutocompletePaginado label={"Captador*"}
                    parameters={{
                        width: '24%',
                        label: "Captador*",
                        disabled: isStatusCancelado || contrato?.semComissao,
                        error: error?.captador,
                        bodyToSearch: { somenteAtivos: true },
                        getLabel: ((item) => item.nome),
                        onChange: (itemSelecionado) => handleChangeColaboradorComissao('captador', 'porcentagemComissaoCaptador', itemSelecionado),
                        value: contrato?.captador,
                        bodyToSearch: { captador: true, somenteAtivos: true }
                    }}
                    endpoint={"colaboradores/paginated"} />

                <Input id="porcentagemComissaoCaptador"
                    value={contrato?.porcentagemComissaoCaptador}
                    parameters={{ error: error.porcentagemComissaoCaptador, width: '5%', type: 'number', min: 0, max: 90, step: 'any', disabled: isStatusCancelado || contrato?.semComissao, label: "%", onChange: (value) => handleInputChange('porcentagemComissaoCaptador', value) }} />

                <AutocompletePaginado label={"Fechador*"}

                    parameters={{
                        width: '24%',
                        error: error?.fechador,
                        label: "Fechador*",
                        disabled: isStatusCancelado || contrato?.semComissao,
                        getLabel: ((item) => item.nome),
                        onChange: (itemSelecionado) => handleChangeColaboradorComissao('fechador', 'porcentagemComissaoFechador', itemSelecionado),
                        value: contrato?.fechador,
                        bodyToSearch: { fechador: true, somenteAtivos: true }
                    }}
                    endpoint={"colaboradores/paginated"} />

                <Input id="porcentagemComissaoFechador"
                    value={contrato?.porcentagemComissaoFechador}
                    parameters={{ error: error.porcentagemComissaoFechador, width: '5%', type: 'number', min: 0, max: 90, step: 'any', disabled: isStatusCancelado || contrato?.semComissao, label: "%", onChange: (value) => handleInputChange('porcentagemComissaoFechador', value) }} />

                <Input id="data"
                    value={contrato?.data}
                    parameters={{ error: error.data, width: '13%', disabled: false, type: 'date', label: "Data do Contrato*", onChange: (value) => handleInputChange('data', value) }} />
            </div>

            <div className='header-body-margin-bottom'>
                <h4 style={{ color: 'black', display: 'inline-block', textShadow: '0 6px 6px rgba(122, 116, 182, 0.5)' }}>Dados financeiros</h4>
            </div>

            <div className='row'>
                <InputMonetary id="valorCausa"
                    value={contrato?.valorCausa}
                    disabled={isStatusCancelado}
                    onChange={(value) => handleInputChange('valorCausa', value)}
                    error={error.valorCausa} type='decimal' width='20%' label="Valor do Endividamento*" />

                <InputMonetary id="honorarioInicial"
                    value={contrato?.honorarioInicial}
                    disabled={isStatusCancelado}
                    error={error.honorarioInicial} width='20%' label="Honorarios Iniciais*" onChange={(value) => handleInputChange('honorarioInicial', value)} />

                <Input id="honorarioFinal"
                    value={contrato?.honorarioFinal}
                    parameters={{ error: error.honorarioFinal, width: '20%', max: 99, type: 'number', disabled: isStatusCancelado, label: "Honorarios Finais (%)*", onChange: (value) => handleInputChange('honorarioFinal', value) }} />


                <Input id="vencimentoPrimeiraParcela"
                    value={contrato?.vencimentoPrimeiraParcela}
                    parameters={{ error: error.vencimentoPrimeiraParcela, width: '20%', type: 'date', disabled: contrato?.id, label: "Vencimento 1º Parcela*", onChange: (value) => handleInputChange('vencimentoPrimeiraParcela', value) }} />
            </div>

            <div className='row-with-auto-complete'>
                <AutocompletePaginado label={"Condição de Recebimento*"}
                    parameters={{
                        width: '20%',
                        label: "Condição de Recebimento*",
                        error: error?.condicaoPagamento,
                        disabled: isStatusCancelado,
                        getLabel: ((item) => (item.condicaoPagamento)),
                        onChange: (condicaoPagamentoSelecionado) => handleChangeCondicaoPagamento(condicaoPagamentoSelecionado?.item),
                        value: contrato?.condicaoPagamento
                    }}
                    endpoint={"contratos/condicoes"} />

                <Input id="quantidadeParcelas"
                    value={contrato?.quantidadeParcelas}
                    parameters={{ error: error.quantidadeParcelas, max: 48, disabled: isCondicaoPagamentoAVista() || isStatusCancelado, width: '20%', type: 'number', label: "Quantidade de Parcelas*", onChange: (value) => handleInputChange('quantidadeParcelas', value) }} />

                <AutocompletePaginado label={"Status*"}
                    parameters={{
                        width: '20%',
                        error: error?.status,
                        disabled: isStatusCancelado && !hasAnyRole(rolesMapping.admin),
                        bodyToSearch: { statusContrato: true },
                        getLabel: ((item) => (item.status)),
                        onChange: (statusSelecionado) => handleInputChange('status', statusSelecionado?.item),
                        value: contrato?.status,
                        inputStyle: (status) => (
                            status ?
                            {
                                color: 'black',
                                // fontWeight: 'bold',
                                backgroundImage: `radial-gradient(circle, ${status?.item?.color || 'black'} 45%, transparent 50%)`,
                                backgroundPosition: '8px center',
                                backgroundRepeat: 'no-repeat',
                                backgroundSize: '14px 14px',
                                paddingLeft: '28px'
                            }
                            : {}
                        ),
                    }}
                    endpoint={"contratos/status"} />

                <AutocompletePaginado label={"Origem*"}
                    parameters={{
                        width: '20%',
                        label: "Origem*",
                        error: error?.origem,
                        disabled: isStatusCancelado,
                        getLabel: ((item) => (item.origem)),
                        onChange: (origemSelecionado) => handleInputChange('origem', origemSelecionado?.item),
                        value: contrato?.origem
                    }}
                    endpoint={"contratos/origens"} />

            </div>


            <div className='row'>
                <Input id="observacao"
                    value={contrato?.observacao}
                    parameters={{ error: error.observacao, width: '100%', label: "Informações Adicionais", disabled: isStatusCancelado, onChange: (value) => handleInputChange('observacao', value) }} />
            </div>

            {!isStatusCancelado ?

                <button onClick={() => salvarContrato()} className="add-button" disabled={isStatusCancelado}>
                    Salvar Contrato
                </button>
                :
                hasAnyRole(rolesMapping.admin) && <button onClick={() => atualizarStatus()} className="add-button" disabled={!isStatusCancelado}>
                    Atualizar Status
                </button>
            }

            {contrato?.id && <div className='header-body-margin-bottom'>
                <h4 style={{ color: 'black', display: 'inline-block', textShadow: '0 6px 6px rgba(122, 116, 182, 0.5)' }}>Detalhamento Pagamentos</h4>
            </div>}

            {contrato?.id && tabs()}

        </div>
    );
}

export default ContratoForm;
